// ============================================================================
// FIRESTORE REPO — Personal Finance V2 (Blueprint §3.5 / ADR-003)
// Adapter que encapsula TODAS as operações Firestore do módulo PF.
// Único arquivo que importa firebase/firestore nesta camada.
// ============================================================================

import {
    collection, doc, writeBatch, getDocs, query, where,
    orderBy, serverTimestamp, updateDoc, getDoc,
} from 'firebase/firestore';
import { db } from '../../../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../../../config/Config';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

const paths = {
    postings:  ['artifacts', APP_ID, 'public', 'data', 'personal_postings'],
    templates: ['artifacts', APP_ID, 'public', 'data', 'personal_recurrence_templates'],
    settings:  ['artifacts', APP_ID, 'public', 'data', 'personal_finance_settings', 'config'],
    auditLog:  ['artifacts', APP_ID, 'public', 'data', 'personal_audit_log'],
};

// --- Helpers internos ---
function postingsCol() { return collection(db, ...paths.postings); }
function auditCol()    { return collection(db, ...paths.auditLog); }

// ============================================================================
// WRITE — Atomic Batch Operations
// ============================================================================

/**
 * Cria N documentos em personal_postings atomicamente (writeBatch).
 * Cada item em `docs` pode ter `id` explícito ou será auto-gerado.
 * @param {Array<{id?: string, data: object}>} docs
 * @returns {Promise<string[]>} IDs dos documentos criados
 */
export async function batchCreatePostings(docs) {
    const batch = writeBatch(db);
    const col = postingsCol();
    const ids = [];

    for (const { id, data } of docs) {
        const ref = id ? doc(db, ...paths.postings, id) : doc(col);
        batch.set(ref, {
            ...data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        ids.push(ref.id);
    }

    await batch.commit();
    return ids;
}

/**
 * Atualiza N documentos em personal_postings atomicamente.
 * @param {Array<{id: string, patch: object}>} updates
 */
export async function batchUpdatePostings(updates) {
    const batch = writeBatch(db);
    for (const { id, patch } of updates) {
        const ref = doc(db, ...paths.postings, id);
        batch.update(ref, { ...patch, updatedAt: serverTimestamp() });
    }
    await batch.commit();
}

/**
 * Append de entradas no audit log (sem batch — append-only).
 * @param {Array<object>} entries
 */
export async function appendAuditLog(entries) {
    const batch = writeBatch(db);
    const col = auditCol();
    for (const entry of entries) {
        const ref = doc(col);
        batch.set(ref, { ...entry, timestamp: serverTimestamp() });
    }
    await batch.commit();
}

// ============================================================================
// READ — Queries
// ============================================================================

/**
 * Busca postings por range de data (campo `date` ou `competenceDate`).
 * @param {string} field — 'date' ou 'competenceDate'
 * @param {string} start — ISO date
 * @param {string} end — ISO date
 * @returns {Promise<object[]>}
 */
export async function queryByDateRange(field, start, end) {
    const q = query(
        postingsCol(),
        where(field, '>=', start),
        where(field, '<=', end),
        orderBy(field, 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Busca um posting pelo ID.
 * @param {string} id
 * @returns {Promise<object|null>}
 */
export async function getPostingById(id) {
    const ref = doc(db, ...paths.postings, id);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Busca postings por pairId.
 * @param {string} pairId
 * @returns {Promise<object[]>}
 */
export async function queryByPairId(pairId) {
    const q = query(postingsCol(), where('pairId', '==', pairId));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Exporta paths para uso no PersonalFinance.jsx (retrocompatibilidade)
export { paths, db };
